import { type NoticeSkin, noticeSkin } from "@shared/push-shared.ts";
import { type ReactNode, useEffect, useState } from "react";
import { UserAvatar } from "../UserAvatar.tsx";

export type NoticeActor = {
  name: string;
  image?: string | null;
  updatedAt?: Date | string | number | null;
};

export function useNoticeSkin(): NoticeSkin {
  const [skin, setSkin] = useState<NoticeSkin>("ios");
  useEffect(() => {
    setSkin(noticeSkin(navigator.userAgent));
  }, []);
  return skin;
}

export function OsNotice({
  title,
  body,
  time,
  unread = true,
  skin,
  actor,
  footer,
  onOpen,
  openDisabled,
}: {
  title: string;
  body?: string;
  time?: string;
  unread?: boolean;
  skin: NoticeSkin;
  actor?: NoticeActor | null;
  footer?: ReactNode;
  onOpen?: () => void;
  openDisabled?: boolean;
}) {
  const ios = skin === "ios";
  const fromPerson = Boolean(actor?.name.trim());
  const sender = fromPerson
    ? actor!.name.trim().split(/\s+/)[0] || actor!.name.trim()
    : "eConnect DM";

  const bodyBlock = (
    <>
      <div className="flex items-baseline justify-between gap-2">
        <span
          className={
            ios
              ? "truncate text-[11px] font-semibold tracking-wide opacity-80"
              : "truncate text-xs text-gray-500"
          }
        >
          {sender}
        </span>
        {time ? (
          <span
            className={ios ? "shrink-0 text-[11px] opacity-60" : "shrink-0 text-xs text-gray-500"}
          >
            {unread ? time : `${time} · Read`}
          </span>
        ) : null}
      </div>
      <p className={`mt-0.5 text-sm leading-snug ${unread ? "font-semibold" : "font-medium"}`}>
        {title}
      </p>
      {body ? (
        <p
          className={
            ios
              ? "mt-0.5 line-clamp-2 text-sm leading-snug opacity-80"
              : "mt-0.5 line-clamp-2 text-sm leading-snug text-gray-500"
          }
        >
          {body}
        </p>
      ) : null}
    </>
  );

  const main = (
    <div className="flex gap-3">
      {fromPerson ? (
        <UserAvatar
          name={actor!.name}
          image={actor!.image}
          updatedAt={actor!.updatedAt}
          size="sm"
          decorative
        />
      ) : (
        <img
          src="/logo-192-192.png"
          alt=""
          width={24}
          height={24}
          className="mt-0.5 h-6 w-6 shrink-0 rounded-md"
        />
      )}
      <div className="min-w-0 flex-1">{bodyBlock}</div>
    </div>
  );

  return (
    <div
      className={
        ios
          ? "edm-os-notice-ios rounded-2xl p-3 shadow-lg"
          : "rounded-lg border border-gray-200 bg-white p-3 text-gray-900 shadow-md"
      }
    >
      {onOpen ? (
        <button
          type="button"
          disabled={openDisabled}
          onClick={onOpen}
          className="w-full text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50"
        >
          {main}
        </button>
      ) : (
        main
      )}
      {footer}
    </div>
  );
}
